<template>
  <div class="company-register-page">
    <div class="register-container">
      <!-- Loading State -->
      <div v-if="loading" class="loading-state">
        <a-spin size="large" />
        <p class="mt-16">Validating registration link...</p>
      </div>

      <!-- Error State -->
      <div v-else-if="error" class="error-state">
        <a-result
          status="error"
          title="Invalid Registration Link"
          :sub-title="error"
        >
          <template #extra>
            <a-button type="primary" @click="$router.push('/login')">
              Go to Login
            </a-button>
          </template>
        </a-result>
      </div>

      <!-- Registration Form -->
      <div v-else class="register-form">
        <div class="form-header">
          <h1>Company Registration</h1>
          <p>Complete your company registration to create your account</p>
        </div>

        <a-form
          :form="form"
          @submit="handleSubmit"
          class="registration-form"
          layout="vertical"
        >
          <a-form-item label="Company Name" required>
            <a-input
              v-decorator="[
                'companyName',
                {
                  rules: [
                    { required: true, message: 'Please enter your company name' },
                    { min: 2, message: 'Company name must be at least 2 characters' },
                    { validator: this.validateCompanyName }
                  ]
                }
              ]"
              placeholder="Enter your company name"
              size="large"
            />
          </a-form-item>

          <a-form-item label="Company Address" required>
            <a-textarea
              v-decorator="[
                'companyAddress',
                {
                  rules: [
                    { required: true, message: 'Please enter your company address' },
                    { min: 10, message: 'Company address must be at least 10 characters' }
                  ]
                }
              ]"
              placeholder="Enter your company address"
              :rows="3"
              size="large"
            />
          </a-form-item>

          <a-form-item label="Contact Person First Name" required>
            <a-input
              v-decorator="[
                'contactFirstName',
                {
                  rules: [
                    { required: true, message: 'Please enter contact person first name' },
                    { min: 2, message: 'First name must be at least 2 characters' }
                  ]
                }
              ]"
              placeholder="Enter contact person first name"
              size="large"
            />
          </a-form-item>

          <a-form-item label="Contact Person Last Name" required>
            <a-input
              v-decorator="[
                'contactLastName',
                {
                  rules: [
                    { required: true, message: 'Please enter contact person last name' },
                    { min: 2, message: 'Last name must be at least 2 characters' }
                  ]
                }
              ]"
              placeholder="Enter contact person last name"
              size="large"
            />
          </a-form-item>

          <a-form-item label="Contact Email" required>
            <a-input
              v-decorator="[
                'contactEmail',
                {
                  rules: [
                    { required: true, message: 'Please enter contact email' },
                    { type: 'email', message: 'Please enter a valid email address' }
                  ]
                }
              ]"
              placeholder="Enter contact email address"
              size="large"
            />
          </a-form-item>

          <a-form-item label="Contact Phone Number" required>
            <a-input
              v-decorator="[
                'contactPhone',
                {
                  rules: [
                    { required: true, message: 'Please enter contact phone number' },
                    { pattern: /^[\+]?[1-9][\d\-]{0,15}$/, message: 'Please enter a valid phone number' }
                  ]
                }
              ]"
              placeholder="Enter contact phone number"
              size="large"
            />
          </a-form-item>

          <a-row :gutter="24">
            <a-col :span="12">
              <a-form-item label="US DOT Number">
                <a-input
                  v-decorator="[
                    'usDotNumber',
                    {
                      rules: [
                        { pattern: /^[0-9]+$/, message: 'DOT number must contain only numbers' },
                        { validator: this.validateDOTNumber }
                      ]
                    }
                  ]"
                  placeholder="Enter US DOT number"
                  size="large"
                />
              </a-form-item>
            </a-col>
            <a-col :span="12">
              <a-form-item label="MC Number">
                <a-input
                  v-decorator="[
                    'mcNumber',
                    {
                      rules: [
                        { pattern: /^[0-9]+$/, message: 'MC number must contain only numbers' },
                        { validator: this.validateMCNumber }
                      ]
                    }
                  ]"
                  placeholder="Enter MC number"
                  size="large"
                />
              </a-form-item>
            </a-col>
          </a-row>

          <a-form-item label="Password" required>
            <a-input-password
              v-decorator="[
                'password',
                {
                  rules: [
                    { required: true, message: 'Please enter a password' }
                  ]
                }
              ]"
              placeholder="Enter your password"
              size="large"
            />
          </a-form-item>

          <a-form-item label="Confirm Password" required>
            <a-input-password
              v-decorator="[
                'confirmPassword',
                {
                  rules: [
                    { required: true, message: 'Please confirm your password' },
                    {
                      validator: this.compareToFirstPassword,
                    }
                  ]
                }
              ]"
              placeholder="Confirm your password"
              size="large"
            />
          </a-form-item>

          <a-form-item>
            <a-button
              type="primary"
              html-type="submit"
              size="large"
              :loading="submitting"
              block
            >
              Create Company Account
            </a-button>
          </a-form-item>
        </a-form>

        <div class="form-footer">
          <p>Already have an account? <a @click="window.location.href='https://tender.gurmanlogistics.com/login'">Sign in</a></p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import companyAPI from '../../api/company';

export default {
  name: 'CompanyRegister',
  data() {
    return {
      loading: true,
      error: '',
      submitting: false,
      token: '',
    };
  },
  beforeCreate() {
    this.form = this.$form.createForm(this);
  },
  async mounted() {
    console.log('CompanyRegister component mounted');
    console.log('Route query:', this.$route.query);
    
    // Get token from URL query parameters
    this.token = this.$route.query.token;
    
    if (!this.token) {
      this.error = 'No registration token provided';
      this.loading = false;
      return;
    }

    // Validate the token
    try {
      await companyAPI.validateRegisterToken(this.token);
      this.loading = false;
    } catch (err) {
      this.error = err.response?.data?.error || 'Invalid or expired registration token';
      this.loading = false;
    }
  },
  methods: {
    compareToFirstPassword(rule, value, callback) {
      const form = this.form;
      if (value && value !== form.getFieldValue('password')) {
        callback('The two passwords that you entered do not match!');
      } else {
        callback();
      }
    },

    // Validate company name uniqueness (client-side check)
    validateCompanyName(rule, value, callback) {
      if (!value) {
        callback();
        return;
      }
      
      // For now, we'll do basic validation. Full uniqueness check will be done server-side
      if (value.length < 2) {
        callback('Company name must be at least 2 characters');
        return;
      }
      
      callback();
    },

    // Validate DOT number uniqueness (client-side check)
    validateDOTNumber(rule, value, callback) {
      if (!value) {
        callback();
        return;
      }
      
      // Basic validation - full uniqueness check will be done server-side
      if (!/^[0-9]+$/.test(value)) {
        callback('DOT number must contain only numbers');
        return;
      }
      
      callback();
    },

    // Validate MC number uniqueness (client-side check)
    validateMCNumber(rule, value, callback) {
      if (!value) {
        callback();
        return;
      }
      
      // Basic validation - full uniqueness check will be done server-side
      if (!/^[0-9]+$/.test(value)) {
        callback('MC number must contain only numbers');
        return;
      }
      
      callback();
    },

    async handleSubmit(e) {
      e.preventDefault();
      
      this.form.validateFields(async (err, values) => {
        if (err) return;

        this.submitting = true;
        
        try {
          const companyData = {
            name: values.companyName,
            address: values.companyAddress,
            contact_first_name: values.contactFirstName,
            contact_last_name: values.contactLastName,
            contact_email: values.contactEmail,
            contact_phone: values.contactPhone,
            us_dot_number: values.usDotNumber || null,
            mc_number: values.mcNumber || null,
            password: values.password,
            register_token: this.token,
          };

          await companyAPI.registerWithToken(companyData);
          
          this.$message.success('Company account created successfully! Please sign in.');
          window.location.href = 'https://tender.gurmanlogistics.com/login';
        } catch (err) {
          const errorMessage = err.response?.data?.error || 'Failed to create company account';
          
          // Handle specific uniqueness errors
          if (errorMessage.includes('duplicate values found')) {
            this.$message.error('Registration failed: ' + errorMessage);
          } else {
            this.$message.error(errorMessage);
          }
        } finally {
          this.submitting = false;
        }
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.company-register-page {
  min-height: 100vh;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;

  .register-container {
    background: white;
    border-radius: 12px;
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
    width: 100%;
    max-width: 600px;
    padding: 40px;

    .loading-state,
    .error-state {
      text-align: center;
      padding: 40px 20px;
    }

    .register-form {
      .form-header {
        text-align: center;
        margin-bottom: 32px;

        h1 {
          font-size: 28px;
          font-weight: 600;
          color: #333;
          margin-bottom: 8px;
        }

        p {
          color: #666;
          font-size: 16px;
          margin: 0;
        }
      }

      .registration-form {
        .ant-form-item {
          margin-bottom: 20px;
        }
      }

      .form-footer {
        text-align: center;
        margin-top: 24px;
        padding-top: 24px;
        border-top: 1px solid #f0f0f0;

        p {
          margin: 0;
          color: #666;

          a {
            color: #1890ff;
            cursor: pointer;
            text-decoration: none;

            &:hover {
              text-decoration: underline;
            }
          }
        }
      }
    }
  }
}

@media (max-width: 768px) {
  .company-register-page {
    .register-container {
      padding: 20px;
      margin: 10px;

      .register-form {
        .form-header {
          h1 {
            font-size: 24px;
          }
        }
      }
    }
  }
}
</style>
